// ============================================================
// TaxReceiptPdf — renders an official Canadian donation receipt
// using jsPDF (dynamically imported to keep the initial bundle
// small). Consumed by:
//   • ReceiptGenerateModal (single generation flow)
//   • ReceiptBatchModal (year-end batch — one PDF per member)
//   • Member re-download from /giving Receipts section
//
// Input contract — `receipt` is the exact object returned by the
// backend `/api/finance/receipts` endpoint. `appSettings` provides
// the church logo (`appLogo`) and app name.
// ============================================================

import { resolveLogo } from "../../lib/AppSettingsContext";

const GOLD = "var(--kc-accent)";

function money(cents, symbol) {
  return `${symbol}${((Number(cents) || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function readableRange(dateFrom, dateTo) {
  try {
    const f = new Date(dateFrom + "T00:00:00");
    const t = new Date(dateTo   + "T00:00:00");
    const fmt = (d) => d.toLocaleDateString("en-CA", { month: "long", day: "numeric" });
    return `${fmt(f)} to ${fmt(t)} ${t.getFullYear()}`;
  } catch { return `${dateFrom} to ${dateTo}`; }
}

async function fetchAsDataUrl(url) {
  const resp = await fetch(url, { cache: "no-store" });
  const blob = await resp.blob();
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload  = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(blob);
  });
}

/** Generate + save a PDF file for the given persisted receipt. */
export async function generateReceiptPdf({ receipt, appSettings, currencySymbol = "$" }) {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginL = 42;
  const marginR = pageW - 42;

  const rs = receipt?.settingsSnapshot || {};
  const churchLegalName  = rs.churchLegalName || appSettings?.appName || "Kingdom Connect";
  const churchAddress    = rs.churchAddress   || "";
  const charityRegNumber = rs.charityRegNumber || "";
  const signatoryName    = rs.signatoryName   || "";
  const signatoryTitle   = rs.signatoryTitle  || "";
  const signatureImage   = rs.signatureImage  || "";
  const statementText    = rs.statementText   || "No goods or services were received in exchange for this donation.";

  // ---- HEADER: church logo top-left; legal name + address top-right ----
  let logoDataUrl = null;
  try {
    const logoUrl = resolveLogo(appSettings?.appLogo, true);
    if (logoUrl) logoDataUrl = await fetchAsDataUrl(logoUrl);
  } catch { /* logo optional */ }
  let y = 48;
  if (logoDataUrl) {
    try { pdf.addImage(logoDataUrl, "PNG", marginL, y - 10, 60, 60); } catch { /* ignore */ }
  }
  // Right-side church legal name + address
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(0);
  pdf.text(churchLegalName, marginR, y + 4, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(70);
  const addressLines = String(churchAddress || "").split("\n").filter(Boolean);
  addressLines.forEach((ln, i) => {
    pdf.text(ln, marginR, y + 20 + i * 12, { align: "right" });
  });

  y += Math.max(60, 40 + addressLines.length * 12);

  // Gold divider
  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(1.1);
  pdf.line(marginL, y, marginR, y);
  y += 22;

  // ---- TITLE: OFFICIAL DONATION RECEIPT centered ----
  pdf.setFont("times", "bold");
  pdf.setFontSize(19);
  pdf.setTextColor(0);
  pdf.text("OFFICIAL DONATION RECEIPT", pageW / 2, y, { align: "center" });
  y += 22;

  // Receipt number + date of issue on the same line
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0);
  pdf.text(`Receipt No: ${receipt.receiptNumber}`, marginL, y);
  pdf.text(`Date of Issue: ${receipt.issuedDate}`, marginR, y, { align: "right" });
  y += 16;

  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.6);
  pdf.line(marginL, y, marginR, y);
  y += 18;

  // ---- MEMBER INFO ----
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0);
  pdf.text("Donor:", marginL, y);
  pdf.setFont("helvetica", "normal");
  pdf.text(receipt.memberName || "—", marginL + 60, y);
  y += 14;

  if (receipt.memberAddress) {
    pdf.setFont("helvetica", "bold");
    pdf.text("Address:", marginL, y);
    pdf.setFont("helvetica", "normal");
    const addr = pdf.splitTextToSize(receipt.memberAddress, marginR - marginL - 70);
    addr.forEach((ln, i) => pdf.text(ln, marginL + 60, y + i * 12));
    y += 14 * Math.max(1, addr.length);
  }

  pdf.setFont("helvetica", "bold");
  pdf.text("Period:", marginL, y);
  pdf.setFont("helvetica", "normal");
  pdf.text(readableRange(receipt.dateFrom, receipt.dateTo), marginL + 60, y);
  y += 20;

  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.6);
  pdf.line(marginL, y, marginR, y);
  y += 16;

  // ---- ITEMIZED TABLE / TOTAL-ONLY ----
  if (receipt.format === "itemized") {
    // Column layout: Date | Category | Amount
    const cDate = marginL;
    const cCat  = marginL + 120;
    const cAmt  = marginR;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(GOLD);
    pdf.text("Date",     cDate, y);
    pdf.text("Category", cCat,  y);
    pdf.text("Amount",   cAmt,  y, { align: "right" });
    y += 6;
    pdf.setDrawColor(GOLD);
    pdf.setLineWidth(0.5);
    pdf.line(marginL, y, marginR, y);
    y += 14;

    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(30);
    const rows = [...(receipt.records || [])].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    for (const r of rows) {
      if (y > pageH - 200) { pdf.addPage(); y = 60; }
      const cat = r.category === "Other" ? (r.categoryOther || "Other") : (r.category || "");
      pdf.text(String(r.date || ""), cDate, y);
      pdf.text(String(cat),           cCat,  y);
      pdf.text(money(r.amountCents, currencySymbol), cAmt, y, { align: "right" });
      y += 14;
    }
    y += 4;
    pdf.setDrawColor(GOLD);
    pdf.setLineWidth(0.8);
    pdf.line(marginL, y, marginR, y);
    y += 18;
  }

  // ---- ELIGIBLE AMOUNT ----
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(0);
  pdf.text("Total Donations:", marginL, y);
  pdf.setTextColor(GOLD);
  pdf.text(money(receipt.totalCents, currencySymbol), marginR, y, { align: "right" });
  y += 18;
  pdf.setTextColor(0);
  pdf.text("Eligible Amount for Tax Purposes:", marginL, y);
  pdf.setTextColor(GOLD);
  pdf.text(money(receipt.totalCents, currencySymbol), marginR, y, { align: "right" });
  y += 22;

  // ---- CHARITY REGISTRATION NUMBER ----
  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.5);
  pdf.line(marginL, y, marginR, y);
  y += 16;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0);
  pdf.text("Canadian Charity Registration Number:", marginL, y);
  pdf.setFont("helvetica", "normal");
  pdf.text(charityRegNumber || "—", marginR, y, { align: "right" });
  y += 20;

  // ---- STANDARD STATEMENT (italic) ----
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(10);
  pdf.setTextColor(50);
  const stmtLines = pdf.splitTextToSize(statementText, marginR - marginL);
  stmtLines.forEach((ln, i) => pdf.text(ln, marginL, y + i * 12));
  y += 12 * stmtLines.length + 22;

  // ---- SIGNATURE ----
  const sigTop = y;
  const sigX   = marginL;
  const sigW   = 200;
  if (signatureImage) {
    try { pdf.addImage(signatureImage, "PNG", sigX, sigTop - 30, sigW, 40); }
    catch { /* if not PNG the addImage fails silently — fallback to line */ }
  }
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.6);
  pdf.line(sigX, sigTop + 12, sigX + sigW, sigTop + 12);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0);
  pdf.text(signatoryName || "Authorized Signatory", sigX, sigTop + 26);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(70);
  pdf.text(signatoryTitle || "", sigX, sigTop + 38);

  // ---- FOOTER (bottom-centered) ----
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(140);
  pdf.text(
    "This receipt is issued in accordance with the Income Tax Act of Canada",
    pageW / 2, pageH - 30, { align: "center" }
  );

  // ---- SAVE ----
  const safeMember = String(receipt.memberName || "Member").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "") || "Member";
  const fname = `Kingdom-Connect-Receipt-${safeMember}-${receipt.year}-${receipt.receiptNumber}.pdf`;
  pdf.save(fname);
  return fname;
}
